import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Stack,
  MenuItem,
  Button
} from '@mui/material';
import { MenuBook as JournalIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Kam Hero Header Component - appears on all Design Studio pages
const KamHeroHeader = ({
  brandId,
  bimesterOptions = [],
  selectedBimester,
  onBimesterChange,
  lookOptions = [],
  selectedLook,
  onLookChange,
  category = 'Outerwear',
  bimesterRevenue = '$120,000',
  cogsBudget = '$20,639',
  perUnitCogs = '$20,639',
  perUnitRetail = '$37,526',
  perLookCogs = '$10,320',
  perLookRetail = '$18,763',
  plannedLooks = '2',
  plannedUnits = '1',
  subtitle = 'Capture product naming, SKU structure, material details, and technical notes for T-Shirts.'
}) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: '#111928',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        px: 4,
        pt: 2,
        pb: 2,
        mb: 0
      }}
    >
      {/* TOP ROW: Kam | Dropdowns | Description */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 3
        }}
      >
        {/* LEFT: Kam */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            component="img"
            src="/Image_Assets/Kam_headshot.png"
            alt="Kam - Assistant Designer"
            sx={{
              width: 120,
              height: 140,
              objectFit: 'cover',
              borderRadius: '4px',
              border: '2px solid rgba(255,255,255,0.1)'
            }}
          />
          <Box>
            <Typography variant="h5" fontWeight={700} color="#fff">
              Kam
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              Assistant Designer
            </Typography>
            {brandId && (
              <Button
                size="small"
                startIcon={<JournalIcon />}
                onClick={() => navigate(`/brands/${brandId}/journal`)}
                sx={{
                  mt: 1,
                  color: '#00d9ff',
                  borderColor: '#00d9ff',
                  fontSize: '0.75rem',
                  py: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(0, 217, 255, 0.1)',
                    borderColor: '#00d9ff'
                  }
                }}
                variant="outlined"
              >
                Journal
              </Button>
            )}
          </Box>
        </Box>

        {/* CENTER: Bimester + Look Dropdowns */}
        <Stack spacing={1.5} sx={{ flex: 1, maxWidth: 450 }}>
          <TextField
            select
            label="Bimester"
            value={selectedBimester || ''}
            onChange={onBimesterChange}
            size="small"
            fullWidth
            sx={{
              bgcolor: '#0d1320',
              borderRadius: 1,
              '& .MuiInputLabel-root': { color: '#9bb0cf' },
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover fieldset': { borderColor: '#3b82f6' },
                '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
              }
            }}
          >
            <MenuItem value="">
              <em>Select Bimester</em>
            </MenuItem>
            {bimesterOptions.map((bimester) => (
              <MenuItem key={bimester.value} value={bimester.value}>
                {bimester.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Look / Garment"
            value={selectedLook || ''}
            onChange={onLookChange}
            size="small"
            fullWidth
            sx={{
              bgcolor: '#0d1320',
              borderRadius: 1,
              '& .MuiInputLabel-root': { color: '#00d9ff' },
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: '#00d9ff' },
                '&:hover fieldset': { borderColor: '#00d9ff' },
                '&.Mui-focused fieldset': { borderColor: '#00d9ff', borderWidth: 2 }
              }
            }}
          >
            <MenuItem value="">
              <em>{lookOptions.length ? 'Select a look' : 'No looks available'}</em>
            </MenuItem>
            {lookOptions.map((look) => (
              <MenuItem key={look.value} value={look.value}>
                {look.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {/* RIGHT: Description Text */}
        <Typography
          variant="body2"
          color="rgba(255,255,255,0.65)"
          sx={{ textAlign: 'right', maxWidth: 250, lineHeight: 1.4 }}
        >
          This workspace mirrors a dedicated design team office. Choose the
          bimester and look you are briefing so Kam can keep the conversation
          anchored to the right drop cycle.
        </Typography>
      </Box>

      {/* METADATA ROW - Financial Details */}
      <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <Stack
          direction="row"
          spacing={3}
          flexWrap="wrap"
          sx={{
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.7)',
            justifyContent: 'flex-start'
          }}
        >
          <Typography variant="caption" color="rgba(255,255,255,0.7)">
            Category: <strong style={{ color: '#fff' }}>{category}</strong>
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.7)">
            Bimester Revenue: <strong style={{ color: '#00d9ff' }}>{bimesterRevenue}</strong>
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.7)">
            Bimester COGS Budget: <strong style={{ color: '#00d9ff' }}>{cogsBudget}</strong>
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.7)">
            Per Unit COGS: <strong style={{ color: '#fff' }}>{perUnitCogs}</strong>
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.7)">
            Per Unit Retail: <strong style={{ color: '#fff' }}>{perUnitRetail}</strong>
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.7)">
            Per Look COGS: <strong style={{ color: '#fff' }}>{perLookCogs}</strong>
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.7)">
            Per Look Retail: <strong style={{ color: '#fff' }}>{perLookRetail}</strong>
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.7)">
            Planned Looks: <strong style={{ color: '#fff' }}>{plannedLooks}</strong>
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.7)">
            Planned Units: <strong style={{ color: '#fff' }}>{plannedUnits}</strong>
          </Typography>
        </Stack>
      </Box>

      {/* SUBTITLE */}
      <Typography
        variant="body2"
        color="rgba(255,255,255,0.6)"
        textAlign="left"
        sx={{ mt: 1.5 }}
      >
        {subtitle}
      </Typography>
    </Box>
  );
};

export default KamHeroHeader;
